import React, { useLayoutEffect } from 'react';
import { setStatusBarBase } from '../../lib/statusBarColor';

/**
 * Colour of the status bar on student screens, and of the top edge of the
 * page. Android paints its status bar with `theme-color` and cannot make it
 * transparent in an installed PWA, so the page is built to continue it: its
 * top row of pixels is exactly this flat colour (see TOP_BAND). The blue-grey
 * is the one the old top halo had at its edges.
 * The launch script in index.html already gives the bar this colour (and the
 * boot splash the same top band) for a student or a visitor, so the first
 * student-styled screen changes nothing; the native splash before it is
 * #0a0a0a (manifest), the one switch happens at the first paint.
 */
export const STUDENT_STATUS_BAR_COLOR = '#383d47';

// Background of the top of the page, as the old halo's edges were (measured
// on a 412px-wide phone capture): the bar colour, stepping down to the dark
// background. Opaque, so it also covers the background image's own (uneven)
// light. No white light on top of it any more: the gradient alone.
// On iOS the page is laid out under the status bar (black-translucent), which
// hides its first safe-area-inset-top pixels: the gradient starts below that
// inset so the first visible row is still the bar colour. On Android the
// inset is 0 and nothing moves.
const SAFE_TOP = 'env(safe-area-inset-top, 0px)';
// Exported for screens that must show the same top edge before/around this
// component (the landing page's intro overlay): same colours, same height.
// index.html's boot splash carries a copy of this gradient (CSS, before any
// JS): keep the two in sync.
export const STUDENT_TOP_BAND_HEIGHT = `calc(${SAFE_TOP} + 320px)`;
export const STUDENT_TOP_BAND = `linear-gradient(to bottom, #383d47 0px, #383d47 ${SAFE_TOP}, ${[
  ['#2a2d37', 65],
  ['#1f222a', 130],
  ['#17181f', 195],
  ['#101216', 260],
  ['rgba(16,18,22,0)', 320],
]
  .map(([color, px]) => `${color} calc(${SAFE_TOP} + ${px}px)`)
  .join(', ')})`;

// Geometry of the fixed top band: its box starts 5px below the top edge.
// Safari 26 hit-tests fixed elements 4px in from each edge to tint its bars,
// and a fixed band at the edge cost ~1.7 s of main-thread paint on every
// update of the page. Measured on an iPhone (03/10, Messages): band at the
// edge 6/6 frozen, lifted 3/3 smooth. Since the 27/09 redesign that added the
// band, taps on the student dashboard went from 80 ms to ~4 s (p75) on Safari
// 26, older iOS unchanged (PostHog INP). Same pixels on screen: the gradient
// is drawn 5px up, and the 5px strip above the box is a shadow in the band's
// top colour (a shadow is not hit-tested).
const BAND_LIFT = 5;
// eslint-disable-next-line react-refresh/only-export-components -- style constant shared with LoadingSpinner, next to the band it describes (dev-only HMR hint)
export const LIFTED_TOP_BAND = {
  top: `${BAND_LIFT}px`,
  height: `calc(${STUDENT_TOP_BAND_HEIGHT} - ${BAND_LIFT}px)`,
  background: `${STUDENT_TOP_BAND} 0 -${BAND_LIFT}px / 100% ${STUDENT_TOP_BAND_HEIGHT} no-repeat`,
  boxShadow: `0 -${BAND_LIFT}px 0 0 ${STUDENT_STATUS_BAR_COLOR}`,
};

// Point <meta name="theme-color"> at the student top colour while a student
// screen is mounted, and put index.html's default back when it goes away (the
// coach side keeps that colour). The default comes from the tag's
// `data-default`, not from the value found at mount: the launch script has
// already switched a student's bar to #0a0a0a by then, and restoring that
// would leave a coach logging in afterwards with a black bar.
// Set through statusBarColor so an open sheet can dim it (useStatusBarDim).
// A layout effect: the bar takes the colour before the screen is painted, so
// it never lags a frame behind the page under it (and it runs before
// App.jsx's BootSplashGate effect, which leaves an owned bar alone).
// Exported for screens drawn with the same top band outside this component
// (LoadingSpinner); `enabled` false leaves the bar alone.
// Several screens can own the bar at once (a page with its own LoadingSpinner
// inside, e.g. ConfirmEmailPage): only the last one to go hands it back, or the
// spinner leaving would grey the bar under a page that is still shown.
let studentBarOwners = 0;

export const useStudentStatusBarColor = (enabled = true) => {
  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return undefined;
    const fallback = meta.getAttribute('data-default') ?? meta.getAttribute('content');
    studentBarOwners += 1;
    setStatusBarBase(STUDENT_STATUS_BAR_COLOR);
    return () => {
      studentBarOwners -= 1;
      if (studentBarOwners > 0) return;
      setStatusBarBase(null);
      if (fallback != null) meta.setAttribute('content', fallback);
    };
  }, [enabled]);
};

/**
 * The student-side page background, shared so every student screen looks the
 * same.
 *
 * The light used to be a white conic halo coming from the top edge. On
 * Android the status bar is an opaque strip the page cannot draw under, so
 * that halo always ended up cut off against it. Now the top of the page
 * starts with the bar's own flat colour and fades down to the dark
 * background; a warm copper glow (the brand accent) sits behind the main
 * content block — on the dashboard, the session-of-the-day card.
 *
 * Render it as the FIRST child of a page wrapper that is `relative` and
 * `overflow-hidden`, then put the page content in a sibling with `relative
 * z-10`. The image and screen blur are `fixed` (viewport-sized); the glow and
 * the desktop-only accents are `absolute` so they scroll with the page.
 * The screen blur keeps the `kaiylo-ambient-screen-blur` class, which drops
 * to 24px on phones — see src/index.css for the iOS renderer-memory rationale.
 */
const StudentAmbientBackground = () => {
  useStudentStatusBarColor();
  return (
    <>
      {/* Image de fond */}
      <div
        style={{
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100vw',
          height: '100dvh',
          backgroundImage: 'url(/background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: 1,
          backgroundColor: '#0a0a0a',
        }}
      />

      {/* Layer blur sur l'écran */}
      <div
        className="kaiylo-ambient-screen-blur"
        style={{
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100vw',
          height: '100dvh',
          backgroundColor: 'rgba(0, 0, 0, 0.01)',
          zIndex: 6,
          pointerEvents: 'none',
          opacity: 1,
        }}
      />

      {/* Bande du haut : couvre la lumière propre de l'image de fond et part
          exactement de la couleur de la barre de statut, donc la barre se
          prolonge dans la page sans cassure. Fixe comme l'image ; au-dessus
          du flou d'écran (z-6), sous le contenu (z-10). Décollée du bord de
          5 px : voir LIFTED_TOP_BAND (gel iPhone sous Safari 26). */}
      <div
        aria-hidden
        data-testid="status-bar-shade"
        style={{
          position: 'fixed',
          left: 0,
          width: '100vw',
          zIndex: 7,
          pointerEvents: 'none',
          ...LIFTED_TOP_BAND,
        }}
      />

      {/* Lueur cuivre derrière le bloc principal (carte « Séance du jour » sur
          l'accueil). Centrée à 560px du haut : assez bas pour que rien de
          lumineux n'atteigne le bord supérieur, donc la barre de statut. */}
      <div
        aria-hidden
        data-testid="ambient-copper-glow"
        style={{
          position: 'absolute',
          left: '50%',
          top: '560px',
          width: '640px',
          height: '640px',
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
          background:
            'radial-gradient(circle, rgba(212,132,90,0.55) 0%, rgba(212,132,90,0.18) 35%, rgba(0,0,0,0) 65%)',
          filter: 'blur(60px)',
          zIndex: 5,
          pointerEvents: 'none',
        }}
      />

      {/* Top glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 w-[120%] max-w-[700px] h-[260px] -translate-x-1/2 rounded-full blur-[120px] hidden md:block"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(60, 60, 60, 0.4) 0%, rgba(0, 0, 0, 1) 100%)',
          opacity: 0.35,
        }}
      />
      {/* Warm orange glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-[26%] -left-[6%] w-[420px] h-[420px] blur-[200px] hidden md:block"
        style={{
          background: 'radial-gradient(circle, rgba(212,132,90,0.6) 0%, rgba(5,5,5,0) 65%)',
          opacity: 0.45,
        }}
      />
      {/* Subtle bottom depth glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-18%] right-[-12%] w-[480px] h-[480px] blur-[230px] hidden md:block"
        style={{
          background: 'radial-gradient(circle, rgba(60,60,60,0.4) 0%, rgba(0,0,0,0) 70%)',
          opacity: 0.25,
        }}
      />
    </>
  );
};

export default React.memo(StudentAmbientBackground);
